import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  Typography,
} from '@mui/material'
import React from 'react'

import LoadIngredientAutocomplete from 'shared/autocomplete/loadIngredientAutocomplete'
import { useDispatch } from 'react-redux'
import { IIngredient } from 'interfaces/IIngredient'
import API from '@aws-amplify/api'
import MuiAlert from '@mui/material/Alert'

import { IStockCard } from 'interfaces/IStockCard'
import {
  STOCK_CARD_TYPE_INGREDIENTS,
  VALUE_GUDANG_BAHAN_BAKU,
  VALUE_IN,
  VALUE_OUT,
} from 'enums/stockCard'
import IncomingStockCards from 'components/premix/incomingStockCards'
import IncomingIngredientStockCard from 'components/stockCards/incomingIngredientStockCard'
import OutgoingIngredientStockCard from 'components/stockCards/outgoingIngredientStockCard'
import StockCardIngredientHistory from 'components/stockCardIngredientHistory'
import PendingPurchaseOrders from 'components/purchaseOrder/pendingPurchaseOrders'
import { TYPE_INGREDIENT } from 'components/purchaseOrder/purchaseOrderForm'
import InventoryShell from 'components/inventory/InventoryShell'

const headCells = [
  {
    id: 'ingredientId',
    numeric: false,
    disablePadding: true,
    label: 'Kode Bahan Baku',
    width: 'medium',
  },
  {
    id: 'ingredientName',
    numeric: false,
    disablePadding: false,
    label: 'Nama Bahan Baku',
    width: 'medium',
  },
  {
    id: 'stock',
    numeric: false,
    disablePadding: false,
    label: 'Stok (kg)',
    width: 'small',
  },
]

const stockCardHeadCells = () => {
  return [
    {
      id: 'stockCardDisplayId',
      numeric: false,
      disablePadding: true,
      label: 'Kode Kartu Stok',
      width: 'small',
    },
    {
      id: 'ingredientId',
      numeric: false,
      disablePadding: true,
      label: 'Kode Bahan Baku',
      width: 'small',
      isPartLevel: true,
    },
    {
      id: 'outgoingDestination',
      numeric: false,
      disablePadding: true,
      label: 'Tujuan',
      width: 'small',
    },
    {
      id: 'batchNumber',
      numeric: false,
      disablePadding: true,
      label: 'No. Bets',
      width: 'small',
    },
    {
      id: 'quantity',
      numeric: false,
      disablePadding: true,
      label: 'Qty Diminta (kg)',
      width: 'small',
      isPartLevel: true,
    },
    {
      id: 'latestStock',
      numeric: false,
      disablePadding: true,
      label: 'Stok Lama (kg)',
      width: 'small',
      isPartLevel: true,
    },
    {
      id: 'confirmedQuantity',
      numeric: false,
      disablePadding: true,
      label: 'Qty Dikonfirmasi (kg)',
      width: 'small',
      isPartLevel: true,
    },
    {
      id: 'createdAt',
      numeric: false,
      disablePadding: true,
      label: 'Tanggal',
      width: 'small',
      isDate: true,
    },
    {
      id: 'confirmedBy',
      numeric: false,
      disablePadding: true,
      label: 'Dikonfirmasi Oleh',
      width: 'small',
    },
    {
      id: 'createdBy',
      numeric: false,
      disablePadding: true,
      label: 'Diminta Oleh',
      width: 'small',
    },
  ]
}

const lastMonth = new Date()
lastMonth.setMonth(lastMonth.getMonth() - 1)

export default function IngredientInventory() {
  const [isLoading, setIsLoading] = React.useState<any>(false)
  const [alertMessage, setAlertMessage] = React.useState('')
  // ingredients state is populated by the autocomplete; kept for potential usage
  // only the setter is required for now
  const [, setIngredients] = React.useState<IIngredient[]>([])

  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] =
    React.useState(false)
  const [isFailureSnackbarOpen, setIsFailureSnackbarOpen] =
    React.useState(false)
  const [ingredientHistory, setIngredientHistory] = React.useState<
    IStockCard[]
  >([])
  const handleIngredientsChange = (ingredients: IIngredient[]) => {
    setIngredients(ingredients)
  }
  const [ingredientData, setIngredientData] =
    React.useState<IIngredient | null>()
  const [isIngredientSelected, setIsIngredientSelected] = React.useState(false)
  const [tabValue, setTabValue] = React.useState('1')

  const handleSnackbarClose = (): void => {
    setIsFailureSnackbarOpen(false)
    setIsSuccessSnackbarOpen(false)
  }

  const loadIngredientHistory = (ingredientId: string): void => {
    setIsLoading(true)
    const param = {
      queryStringParameters: {
        pageSize: 9999999,
        isConfirmed: true,
        type: STOCK_CARD_TYPE_INGREDIENTS,
      },
    }

    API.get(
      'products',
      `/premix/getStockCard?ingredientId=${ingredientId}`,
      param
    )
      .then((response: { stockCards: IStockCard[] }) => {
        setIngredientHistory(response.stockCards)
      })
      .catch((error) => {
        // Error fetching ingredient history
        setAlertMessage('Gagal mendapatkan sejarah bahan baku.')
        setIsFailureSnackbarOpen(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const dispatch = useDispatch()
  React.useEffect(() => {
    dispatch({ type: 'SET_PAGE_TITLE', payload: 'Gudang Bahan Baku' })
  }, [dispatch])

  return (
    <Box>
      <InventoryShell
        tabs={[
          { value: '0', label: 'Konfirmasi Kartu Stok Masuk' },
          { value: '4', label: 'Konfirmasi Kartu Stok Keluar' },
          { value: '1', label: 'Tabel Stok Bahan Baku' },
          { value: '5', label: 'Daftar PO Pending' },
        ]}
        tabValue={tabValue}
        onTabChange={(v) => setTabValue(v)}
      >
        {tabValue === '0' && (
          <IncomingStockCards
            type={VALUE_GUDANG_BAHAN_BAKU}
            inOutDirection={VALUE_IN}
            qtyLabelIn={'Qty Mau Dimasukan'}
          />
        )}

        {tabValue === '1' && (
          <Paper>
            <Box p={2}>
              <LoadIngredientAutocomplete
                getMatchedData={handleIngredientsChange}
                loadAll
                showDetailedTable
                onChange={(e) => {
                  setIngredientData(e)
                  setIsIngredientSelected(true)
                  loadIngredientHistory(e.ingredientId)
                }}
              />
            </Box>
            <Box mt={1} />
          </Paper>
        )}

        {tabValue === '4' && (
          <IncomingStockCards
            type={VALUE_GUDANG_BAHAN_BAKU}
            inOutDirection={VALUE_OUT}
          />
        )}

        {tabValue === '2' && <IncomingIngredientStockCard />}
        {tabValue === '3' && <OutgoingIngredientStockCard />}
        {tabValue === '5' && <PendingPurchaseOrders type={TYPE_INGREDIENT} />}
      </InventoryShell>

      <Snackbar
        open={isSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={isFailureSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Dialog
        fullWidth
        maxWidth="xl"
        open={isIngredientSelected}
        onClose={() => {
          setIsIngredientSelected(false)
          setIngredientData(null)
        }}
      >
        <DialogTitle>
          <Typography variant="h4">Bahan Baku Stok History</Typography>
        </DialogTitle>
        <DialogContent>
          <StockCardIngredientHistory
            ingredientId={ingredientData?.ingredientId ?? ''}
            data={ingredientData ? [ingredientData] : []}
            history={ingredientHistory}
            loadHistory={loadIngredientHistory}
            isLoading={isLoading}
            alertMessage={alertMessage}
            // @ts-ignore
            headCells={headCells}
            stockCardHeadCells={stockCardHeadCells()}
            searchingFor={`Kode Kemasan: ${ingredientData?.premixId ?? ''}`}
          />{' '}
        </DialogContent>
      </Dialog>
    </Box>
  )
}
